import React, {useEffect, useState} from 'react';
import Modal from 'react-bootstrap/Modal';

import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import Link from '@material-ui/core/Link';
import InputAdornment from '@material-ui/core/InputAdornment';
import { useSelector } from 'react-redux';
import TextInput from "../Fields/TextInput";
import {useTranslation} from "react-i18next";

// Formats whose export downloads a single named file client-side (see exportDocs in
// Discover.js). The rest of the downloadTypes this modal handles (Clustal, LifeQuest,
// FullText, PatentStats, SeqDb) don't produce a plain named download here, so they don't
// get this generic file name field.
const FILE_EXTENSIONS = {
	XML: '.xml',
	BizInt: '.bpd',
	Geneious: '.geneious',
	Fastafrag: '.txt',
	Fastasub: '.txt',
};

const MAX_FILE_NAME_LENGTH = 150;

const useStyles = makeStyles((theme) => ({
	modalClassConDSI: {
		position: 'absolute',
		width: '105%',
		padding: '35px 15px 0px',
		right: 'auto',
		bottom: 'auto',
	},
	modalClassConDSIDark: {
		position: 'absolute',
		width: '105%',
		padding: '35px 15px 0px',
		right: 'auto',
		bottom: 'auto',
		backgroundColor: '#292929'
	},
	modalBoxContent: {
		maxHeight: '600px',
	},
	modalText: {
		position: 'absolute',
		top: '10px',
		right: '10px'
	},
}));

const DiscoverExportChoicesModal = props => {
	const { t } = useTranslation("common");
	const [choice, setChoice] = useState('all');
	const [option, setOption] = useState('sf');
	const [type, setType] = useState('subject');
	const [title, setTitle] = useState(props.defaultTitle || '');
	const [saveNameError, setSaveNameError] = useState(false);
	const [fileName, setFileName] = useState('');
	const [fileNameError, setFileNameError] = useState(false);
	const fileNameRef = React.useRef(null);
	const classes = useStyles();
	const toggle = useSelector(state => state.setToggle.toggle);

	useEffect(() => {
		if (props.defaultTitle) {
			setTitle(props.defaultTitle);
		}
	}, [props.defaultTitle])

	// When the export dialog opens, default the selection to "N selected alignment(s)" if the
	// user has checked any rows; otherwise fall back to "All". The Modal stays mounted and is
	// only toggled via `show`, so this must run on each open rather than relying on the initial
	// useState value.
	useEffect(() => {
		if (props.showModal) {
			setChoice(props.selectedAlignmentsCount > 0 ? 'selected' : 'all');
			if (FILE_EXTENSIONS[props.downloadType]) {
				setFileName((props.resInfo?.title || '').slice(0, MAX_FILE_NAME_LENGTH));
				setFileNameError(false);
			}
		}
	}, [props.showModal, props.selectedAlignmentsCount, props.downloadType, props.resInfo])

	const changeFileName = (e) => {
		const val = e.target.value.slice(0, MAX_FILE_NAME_LENGTH);
		setFileName(val);
		setFileNameError(!val || val.trim().length === 0);
	}

	const callback = () => {
		if (props.downloadType === 'SeqDb') {
			// Get by DOM instead of title, especially for the default value case
			//const val = document.getElementById("title").value;
			if (title && title.trim().length > 0 && title.length <= 188) {
				setSaveNameError(false);
				props.actionModal(props.fieldConfig, props.filter, props.resInfo, props.downloadType, choice, option, type, title);
			} else {
				setSaveNameError(true);
			}
		}
		else if (props.downloadType === 'PatentStats') {
			(console.log('still working on it'));
			// const htmlUrl = (process.env.REACT_APP_API_URL + url.patentStats)
			// 	.replace('**', props.workflowId);
			// console.log('html', htmlUrl);
			// window.open(htmlUrl, "_blank");
			const urlPath = `#/ExportPatentStatistics/:${encodeURIComponent(props.workflowId)}`;
			window.open(urlPath, '_blank', 'noopener,noreferrer');
			props.closeModal();
		}
		else if (FILE_EXTENSIONS[props.downloadType]) {
			if (fileName && fileName.trim().length > 0) {
				setFileNameError(false);
				props.actionModal(props.fieldConfig, props.filter, props.resInfo, props.downloadType, choice, option, type, undefined, fileName.trim());
			} else {
				setFileNameError(true);
			}
		}
		else {
			props.actionModal(props.fieldConfig, props.filter, props.resInfo, props.downloadType, choice, option, type);
		}
	}

	const changeSelection = (e) => {
		setChoice(e.target.value);
	}

	const changeOption = (e) => {
        setOption(e.target.value);
    }

	const changeType = (e) => {
		setType(e.target.value);
		// Change database name accordingly
		if (props.downloadType === 'SeqDb') {
			//const val = document.getElementById("title").value;
			if (title && (title === props.defaultTitle
				|| title === props.defaultTitle.replace('subjects', 'queries')
				|| title === props.defaultTitle.replace('subjects', 'no-hits'))) {
				if (e.target.value === 'subject') {
					setTitle(props.defaultTitle);
				} else if (e.target.value === 'subjectAll') {
					setTitle(props.defaultTitle.replace('subjects', 'full subjects'));
				}  else if (e.target.value === 'query') {
					setTitle(props.defaultTitle.replace('subjects', 'queries'));
				} else {
					setTitle(props.defaultTitle.replace('subjects', 'no-hits'));
				}
			}
		}
	}

	const changeTitle = (e) => {
		checkTitle(e.target.value);
	}

	function checkTitle(title) {
		let val = title;
		setTitle(val);
		if (!val || val.trim().length === 0 || val.length > 188) {
			setSaveNameError(true);
		} else {
			setSaveNameError(false);
		}
	}

	return (
		<Modal
			size="lg"
			aria-labelledby="contained-modal-title-vcente"
			centered
			show={props.showModal}
			contentClassName={toggle ? classes.modalClassConDSI : classes.modalClassConDSIDark}
			onEntered={() => fileNameRef.current?.focus()}
		>
			<Link href="#" onClick={(e) => e.preventDefault()} className={classes.modalText}>
				<CloseIcon  style = {{ fill : '#777777'}} onClick={props.closeModal} />
			</Link>
			<br></br>
			<div className={toggle? "jumbotron appTextColor": "jumbotronDark appTextColor" } >
				{props.downloadType !== 'SeqDb' &&
					<h2 >You are about to export alignments to {props.downloadTypeDesc}</h2>
				}
				{props.downloadType === 'SeqDb' &&
					<h2 >You are about to Extract Sequence Database</h2>
				}
				{props.downloadType === 'Clustal' &&
				<p className="mb-3" style={{fontSize: '21px',fontFamily: 'Helvetica', fontWeight: '200', wordSpacing: '-2px' }}>
                    Your Clustal file is generated in the background.
                    You can continue to work on your results.
                    As soon as it is ready, we will open a new tab for you to review.
                </p>}
				{props.downloadType === 'SeqDb' &&
					<p className="mb-3" style={{fontSize: '21px',fontFamily: 'Helvetica', fontWeight: '200', wordSpacing: '-2px' }}>
						Your Sequence Database is generated in the background.
						You can continue to work on your results.
						As soon as it is ready, we will send an email to you.
					</p>
				}

				{props.downloadType === 'SeqDb' && <div className="filter-widget-textarea">
					<label style={{padding: '0px'}}>Extract as:</label>
					<TextInput id="title" name="title" variant="outlined" onChange={changeTitle} value={title}
							   inputProps={{maxLength: 188}} defaultValue={title || props.defaultTitle}
							   error={saveNameError} fullWidth={true} autoFocus={true} spellcheck="false"
							   helperText={saveNameError ? t('required') : ""}/>
					{/*<div>
						<label style={{paddingTop: '10px'}}>
							<input id="subjectAll" type="radio" value="subjectAll" name="exportType" defaultChecked={type === 'subjectAll'} onChange={changeType}/>
							<span htmlFor="subjectAll"> All Patent Sequences (complete sequence listing for each patent)</span>
						</label>
					</div>*/}
					<div>
						<label style={{paddingTop: '10px'}}>
							<input id="subject" type="radio" value="subject" name="exportType" defaultChecked={type === 'subject'} onChange={changeType}/>
							<span htmlFor="subject"> Only Patent Sequences matching the query(ies)</span>
						</label>
					</div>
					<div>
						<label style={{padding: '0px'}}>
							<input id="query" type="radio" value="query" name="exportType" defaultChecked={type === 'query'} onChange={changeType}/>
							<span htmlFor="query"> Query Sequences</span>
						</label>
					</div>
					<div>
						<label style={{padding: '0px'}}>
							<input id="queryNohits" type="radio" value="queryNohits" name="exportType" defaultChecked={type === 'queryNohits'} onChange={changeType}
								   disabled={props.queryNohits <= 0}/>
							<span htmlFor="queryNohits"> {props.queryNohits} Queries that did not hit</span>
						</label>
					</div>
				</div>
				}
				{props.downloadType === 'SeqDb' && <br></br>}
				{(props.downloadType !== 'SeqDb' || (props.downloadType === 'SeqDb' && type !== 'queryNohits')) &&
					<div className="filter-widget-textarea">
						<label style={{padding: '0px'}}>Selections:</label>
						{props.selectedAlignmentsCount > 0 &&
							<div>
								<label style={{padding: '0px'}}>
									<input id="selected" type="radio" value="selected" name="exportGroup" checked={choice === 'selected'} onChange={changeSelection}/>
									<span htmlFor="selected"> {Number(props.selectedAlignmentsCount).toLocaleString()} selected alignment(s)</span>
								</label>
							</div>
						}
						{(props.displayCustomSelection && props.downloadType !== 'PatentStats') &&
							<div>
								<label style={{padding: '0px'}}>
									<input id="custom" type="radio" value="custom" name="exportGroup" checked={choice === 'custom'} onChange={changeSelection}/>
									<span htmlFor="custom"> {props.customSelectionLabel}</span>
								</label>
							</div>
						}
						<div>
							<label style={{padding: '0px'}}>
								<input id="all" type="radio" value="all" name="exportGroup" checked={choice === 'all'} onChange={changeSelection}/>
								<span htmlFor="all"> All {Number(props.totalAlignmentNumber).toLocaleString()} alignment(s)</span>
							</label>
							{props.downloadType === 'PatentStats' && (
								<div>
									{console.log('patent statistics props',props)}
									<label style={{ padding: '0px' }}>
										<input id="top3" type="radio" value="top3" name="exportGroup" checked={choice === 'top3'} onChange={changeSelection} />
										<span htmlFor="top3"> Top 3 alignment(s) per query (3 total)</span>
									</label>
								</div>
							)}
						</div>
					</div>
				}

                {props.downloadType === 'Clustal' && <br></br>}
				{props.downloadType === 'Clustal' && <div className="filter-widget-textarea">
                    <label style={{padding: '0px'}}>Options:</label>
                    <div>
                        <label style={{padding: '0px'}}>
                            <input id="sf" type="radio" value="sf" name="exportOption" defaultChecked={option === 'sf'} onChange={changeOption}/>
                            <span htmlFor="sf"> Both Query Sequences and Subject Matching Fragments</span>
                        </label>
                    </div>
                    <div>
                        <label style={{padding: '0px'}}>
                            <input id="both" type="radio" value="both" name="exportOption" defaultChecked={option === 'both'} onChange={changeOption}/>
                            <span htmlFor="both"> Both Query and Subject Sequences</span>
                        </label>
                    </div>
                    <div>
                        <label style={{padding: '0px'}}>
                            <input id="qs" type="radio" value="qs" name="exportOption" defaultChecked={option === 'qs'} onChange={changeOption}/>
                            <span htmlFor="qs"> Query Sequences</span>
                        </label>
                    </div>
                    <div>
                        <label style={{padding: '0px'}}>
                            <input id="ss" type="radio" value="ss" name="exportOption" defaultChecked={option === 'ss'} onChange={changeOption}/>
                            <span htmlFor="ss"> Subject Sequences</span>
                        </label>
                    </div>
                </div>
                }

				<br></br>
				{choice === 'selected' && props.downloadType !== 'Clustal' && props.downloadType !== 'SeqDb' && props.selectedAlertMessage && props.selectedAlertMessage !== '' &&
					<b style={{color: 'red'}}>
						{props.selectedAlertMessage}
					</b>
				}
				{choice === 'selected' && props.downloadType === 'Clustal' && props.selectedAlignmentsCount > parseInt(process.env.REACT_APP_CLUSTAL_LIMIT, 10) &&
					<b style={{color: 'red'}}>
						Maximum data export limit reached. Only first {Number(process.env.REACT_APP_CLUSTAL_LIMIT).toLocaleString()} of {Number(props.selectedAlignmentsCount).toLocaleString()} alignments will be exported.
					</b>
				}
				{choice === 'all' && props.downloadType !== 'Clustal' && props.downloadType !== 'SeqDb' && props.alertMessage && props.alertMessage !== '' &&
					<b style={{color: 'red'}}>
						{props.alertMessage}
					</b>
				}
				{choice === 'all' && props.downloadType === 'Clustal' && props.totalAlignmentNumber > parseInt(process.env.REACT_APP_CLUSTAL_LIMIT, 10) &&
					<b style={{color: 'red'}}>
						Maximum data export limit reached. Only first {Number(process.env.REACT_APP_CLUSTAL_LIMIT).toLocaleString()} of {Number(props.totalAlignmentNumber).toLocaleString()} records will be exported.
					</b>
				}
				{choice === 'custom' && props.downloadType !== 'Clustal' && props.downloadType !== 'SeqDb' && props.customAlertMessage && props.customAlertMessage !== '' &&
					<b style={{color: 'red'}}>
						{props.customAlertMessage}
					</b>
				}
				{choice === 'custom' && props.downloadType === 'Clustal' && props.customTtlCount > parseInt(process.env.REACT_APP_CLUSTAL_LIMIT, 10) &&
					<b style={{color: 'red'}}>
						Maximum data export limit reached. Only first {Number(process.env.REACT_APP_CLUSTAL_LIMIT).toLocaleString()} of {Number(props.customTtlCount).toLocaleString()} results will be exported.
					</b>
				}
				{FILE_EXTENSIONS[props.downloadType] && <div className="filter-widget-textarea" style={{ border: 'none' }}>
					<label style={{padding: '0px'}}>File name:</label>
					<TextInput id="exportChoicesFileName" name="exportChoicesFileName" variant="outlined" inputRef={fileNameRef} onChange={changeFileName}
							   value={fileName} inputProps={{maxLength: MAX_FILE_NAME_LENGTH}} error={fileNameError} fullWidth={true}
							   spellcheck="false" helperText={fileNameError ? t('required') : ""}
							   InputProps={{endAdornment: <InputAdornment position="end">{FILE_EXTENSIONS[props.downloadType]}</InputAdornment>}}/>
				</div>}

				<br></br>
				<div style={{padding: '10px'}}>
					{props.downloadType !== 'SeqDb' && props.downloadType !== 'PatentStats' && props.totalAlignmentNumber > 0 &&
					<button onClick={callback} className="pull-right large-button gqblue">Start Export</button>
					}
					{props.downloadType === 'SeqDb' &&
						<button onClick={callback} className="pull-right large-button gqblue">Start Extract</button>
					}
					{/* Add the Create Report button for Patent Statistics */}
					{props.downloadType === 'PatentStats' &&
						<button onClick={callback} className="pull-right large-button gqblue">Create Report</button>
					}
					<button onClick={props.closeModal} className="pull-right large-button grey">Cancel</button>
				</div>
			</div>
		</Modal>
	);
}

export default DiscoverExportChoicesModal;
